import { inject, injectable } from "@launchtray/tsyringe-async";
import { Bindable, NotifyProperty, nameof } from "bindable-data";
import { IGuestModel } from "./IGuestModel";
import { INetworkClient } from "../../../networking/INetworkClient";
import { GuestProfileDto } from "../../../networking/data/responses/guests/GuestProfileDto";
import { Texture } from "pixi.js";
import { VariableSaver } from "by/gamefactory/tools/variableSaver/VariableSaver";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { IConnectionModel } from "../../../networking/IConnectionModel";
import { NoCacheDynamicAssets } from "../resources/NoCacheDynamicAssets";
import { Sentry } from "../../../../analytics/Sentry";

@injectable()
export class GuestModel extends Bindable implements IGuestModel {
    public readonly id: NotifyProperty<string>;
    public readonly name: NotifyProperty<string>;
    public readonly currentAvatarUrl: NotifyProperty<string>;
    public readonly avatarTexture: NotifyProperty<Texture>;
    public readonly avatarTextures: NotifyProperty<Array<Texture>>;
    public readonly avatarUrls: NotifyProperty<Array<string>>;
    public readonly email: NotifyProperty<string>;

    private readonly avatarAssets: NoCacheDynamicAssets;

    public constructor(
        @inject("INetworkClient") 
        private readonly networkClient: INetworkClient,
        @inject("IConnectionModel") 
        private readonly connectionModel: IConnectionModel,
        private readonly variableSaver: VariableSaver,
        private readonly sentry: Sentry,
        @inject("ILogger") 
        private readonly logger: ILogger
    ) {
        super();
        this.avatarAssets = new NoCacheDynamicAssets("guest_avatars", true);
        this.id = new NotifyProperty<string>(this, nameof(this, "id"), "");
        this.name = new NotifyProperty<string>(this, nameof(this, "name"), "");
        this.currentAvatarUrl = new NotifyProperty<string>(this, nameof(this, "currentAvatarUrl"), "");
        this.avatarTexture = new NotifyProperty<Texture>(this, nameof(this, "avatarTexture"), Texture.EMPTY);
        this.avatarUrls = new NotifyProperty<Array<string>>(this, nameof(this, "avatarUrls"), []);
        this.email = new NotifyProperty<string>(this, nameof(this, "email"), "");
    }

    public async loadGuestData(): Promise<void> {
        try {
            if (!this.connectionModel.connected.value) {
                return;
            }
            const guestProfileDto = await this.networkClient.getGuestProfile();
            if (guestProfileDto.id == undefined
                || guestProfileDto.id == ""
            ) {
                throw new Error("Failed to receive correct guest profile.");
            }
            this.saveLocalStorageData(guestProfileDto);
            this.updateValuesFromDto(guestProfileDto);
            this.sentry.addTag("guest_id", guestProfileDto.id);
            await this.loadAvatars();
        } catch (e: unknown) {
            this.logger.error(e);
        }
    }

    public async switchAvatar(index: number): Promise<void> {
        const avatarUrl = this.avatarUrls.value[index];
        this.avatarAssets.add(avatarUrl);
        await this.avatarAssets.load();
        const previousAvatarUrl = this.currentAvatarUrl.value;
        this.currentAvatarUrl.setValue(avatarUrl);
        this.avatarTexture.setValue(this.avatarAssets.resource(avatarUrl));
        this.avatarAssets.remove(previousAvatarUrl);
    }

    public async changeGuestName(firstName: string): Promise<void> {
        this.name.setValue(firstName);
    }

    public async saveGuestProfile(): Promise<void> {
        const guestProfileDto = await this.networkClient.updateGuestProfile(this.name.value, this.currentAvatarUrl.value);
        this.updateValuesFromDto(guestProfileDto);
        this.avatarTexture.setValue(this.avatarAssets.resource<Texture>(this.currentAvatarUrl.value));
    }

    private updateValuesFromDto(guestDto: GuestProfileDto): void {
        this.id.setValue(guestDto.id);
        this.name.setValue(guestDto.firstName);
        this.currentAvatarUrl.setValue(guestDto.avatarUrl);
        this.avatarUrls.setValue(guestDto.avatars);
        this.email.setValue(guestDto.email);
    }

    private saveLocalStorageData(guestProfileDto: GuestProfileDto): void {
        this.variableSaver.saveString("_CVT_GUEST_ID", guestProfileDto.id);
        if (this.variableSaver.exist("communityCode")) {
            this.variableSaver.removeItem("communityCode");
        }
        if (guestProfileDto.communityCode != null && guestProfileDto.communityCode != "") {
            this.variableSaver.saveString("communityCode", guestProfileDto.communityCode);
        }
        if (this.variableSaver.exist("referrerId")) {
            this.variableSaver.removeItem("referrerId");
        }
        if (guestProfileDto.referrerId != null && guestProfileDto.referrerId != "") {
            this.variableSaver.saveString("referrerId", guestProfileDto.referrerId);
        }
    }

    private async loadAvatars(): Promise<void> {
        this.avatarAssets.add(this.currentAvatarUrl.value);
        await this.avatarAssets.load();
        this.avatarTexture.setValue(this.avatarAssets.resource(this.currentAvatarUrl.value));
    }
}
